import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiFetchBlob, ApiError } from "./client";
import type { ResolvedImages, ResolvedPhoto } from './produktbilder';

// Typen — passen 1:1 zu Backend src/modules/product-knowledge/service.ts
// (getKnowledgeHead / getEvidenceRows / harvestArticle). Drizzle-Select-Projektion,
// kein snake_case-Response — camelCase durchgereicht wie bei portalListings.ts.

export type KnowledgeStatus = "auto" | "locked" | "none";

export interface KnowledgeVersionMeta {
  versionNo: number;
  reason: string;
  createdAt: string;
  createdBy: string;
}

export interface KeywordAnalysisEntry {
  keyword: string;
  reason: string;
}

export interface RecommendedTitleEntry {
  portal: string;
  title: string;
}

// B1 (2026-07-20): KI-abgeleitete Merkmale leben jetzt in product_knowledge.ai_characteristics
// (Migration 0101) -- bewusst GETRENNT von der DR-gemasterten stock_characteristics (Reiter
// "Merkmale" in GrundartikelDetail.tsx, useCharacteristics), damit der taegliche DR-Vollabruf
// sie nicht lautlos loeschen kann. Nur HIER (KI-Analyse-Panel) sichtbar, analog zu
// keywordsAnalysis/recommendedTitles.
export interface AiCharacteristicEntry {
  type: string | null;
  value: string | null;
}

export interface KnowledgeHead {
  images?: ResolvedImages;
  stockItemId: number;
  productType: string | null;
  material: string | null;
  useCase: string | null;
  targetAudience: string | null;
  sellingPoints: string[];
  seoDescriptionHtml: string | null;
  seoDescriptionPlain: string | null;
  keywordsAnalysis: KeywordAnalysisEntry[];
  recommendedTitles: RecommendedTitleEntry[];
  aiCharacteristics: AiCharacteristicEntry[];
  status: KnowledgeStatus;
  enrichedAt: string | null;
  lockedAt: string | null;
  lockedBy: string | null;
  versions: KnowledgeVersionMeta[];
}

export interface EvidenceCard {
  portal: string | null;
  accountKey: string | null;
  listingRef: string | null;
  hasListingHtml: boolean;
  title: string | null;
  descriptionPlain: string | null;
  keywordsRaw: string[];
  attributes: { type: string; value: string }[];
  images: EvidenceImageRef[];
  sourceUrl: string | null;
  harvestedAt: string;
}

export interface EvidenceImageRef {
  url: string;
  fileHash: string | null;
  sharepointPath: string | null;
}

// Kuratierter Unikat-Satz (Bild-Kuratierung): portaluebergreifende, entdoppelte Produktbilder
// mit Google-konformem SEO-Namen. Passt 1:1 zu Backend service.ts getCanonicalImages.
export interface CanonicalImage {
  publicUrl?: string;
  sourceSku?: string;
  match?: ResolvedPhoto['match'];
  variantSku?: string | null;
  usageId?: number | null;
  fileHash: string;
  seoName: string | null;
  altText: string | null;
  sourcePortals: string[];
  originalUrl?: string | null;
  origin: string;
}

export interface ListingHtmlIdentity {
  portal: string;
  accountKey: string;
  listingRef: string;
}

export interface ListingHtmlArchive {
  rawHtml: string;
  editedHtml: string | null;
  fetchedAt: string;
  contentHash: string;
}

export function productKnowledgeImagePath(fileHash: string): string {
  return `/product-knowledge/image/${encodeURIComponent(fileHash)}/`;
}

export function fetchProductKnowledgeImage(fileHash: string, localOnly = false): Promise<Blob> {
  return apiFetchBlob(productKnowledgeImagePath(fileHash) + (localOnly ? '?local=1' : ''));
}

export function useListingHtml(identity: ListingHtmlIdentity | null) {
  const portal = identity?.portal.toLowerCase() ?? "";
  const accountKey = identity?.accountKey ?? "";
  const listingRef = identity?.listingRef ?? "";

  return useQuery({
    queryKey: ["product-knowledge-listing-html", portal, accountKey, listingRef],
    queryFn: () =>
      api.get<ListingHtmlArchive>(
        `/product-knowledge/listing-html/?portal=${encodeURIComponent(portal)}` +
          `&account=${encodeURIComponent(accountKey)}&ref=${encodeURIComponent(listingRef)}`,
      ),
    enabled: Boolean(portal && accountKey && listingRef),
    // Ein fehlendes Archiv ist ein erwarteter Leerstand und soll ohne Retry-Verzoegerung
    // sofort als ruhiger Hinweis erscheinen. Transportfehler duerfen kurz wiederholt werden.
    retry: (failureCount, queryError) =>
      !(queryError instanceof ApiError && queryError.status === 404) && failureCount < 2,
  });
}

export interface HarvestFailure {
  portal: string;
  accountKey: string;
  listingRef: string;
  error: string;
}

export interface HarvestSummary {
  scope: string;
  total: number;
  inserted: number;
  updated: number;
  unchanged: number;
  failed: HarvestFailure[];
}

export interface UnderstandingPatch {
  product_type?: string | null;
  material?: string | null;
  use_case?: string | null;
  target_audience?: string | null;
  selling_points?: string[];
}

/** Wird geworfen, wenn POST /:sku/enrich/ ohne force auf einen gesperrten
 *  (Operator-bearbeiteten) Datensatz trifft (409 LOCKED). Der ApiError.details
 *  aus client.ts traegt lockedBy/lockedAt — hier typisiert durchgereicht, damit
 *  die UI keinen `unknown`-Cast noetig hat. */
export class EnrichLockedError extends Error {
  constructor(
    public readonly lockedBy: string | null,
    public readonly lockedAt: string | null,
  ) {
    super("Datensatz ist gesperrt (Operator-Bearbeitung)");
    this.name = "EnrichLockedError";
  }
}

function toEnrichLockedError(err: unknown): EnrichLockedError | null {
  if (err instanceof ApiError && err.code === "LOCKED") {
    return new EnrichLockedError(
      (err.details.lockedBy as string | null | undefined) ?? null,
      (err.details.lockedAt as string | null | undefined) ?? null,
    );
  }
  return null;
}

export function useProductKnowledge(sku: string) {
  return useQuery({
    queryKey: ["product-knowledge", sku],
    queryFn: () => api.get<KnowledgeHead>(`/product-knowledge/${encodeURIComponent(sku)}/`),
    enabled: Boolean(sku),
  });
}

export function useProductKnowledgeEvidence(sku: string) {
  return useQuery({
    queryKey: ["product-knowledge-evidence", sku],
    queryFn: () => api.get<EvidenceCard[]>(`/product-knowledge/${encodeURIComponent(sku)}/evidence/`),
    enabled: Boolean(sku),
  });
}

/** Kuratierter Unikat-Satz (portaluebergreifend) — analog useProductKnowledgeEvidence. */
export function useProductKnowledgeImages(sku: string, enabled = true) {
  return useQuery({
    queryKey: ["product-knowledge-images", sku],
    queryFn: () => api.get<CanonicalImage[]>(`/product-knowledge/${encodeURIComponent(sku)}/images/`),
    enabled: Boolean(sku) && enabled,
  });
}

export function useHarvestKnowledge(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ result: HarvestSummary }>(`/product-knowledge/${encodeURIComponent(sku)}/harvest/`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['product-images'] });
      void qc.invalidateQueries({ queryKey: ["product-knowledge-evidence", sku] });
      void qc.invalidateQueries({ queryKey: ["product-knowledge", sku] });
    },
  });
}

/** Wirft `EnrichLockedError` statt der rohen ApiError, wenn der Datensatz ohne
 *  force gesperrt ist — die UI faengt genau diesen Typ ab, um den Bestaetigungs-
 *  Dialog zu zeigen (Spec §8 "Aktionen"). Alle anderen Fehler gehen unveraendert durch. */
export function useEnrichKnowledge(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (opts?: { force?: boolean }) => {
      try {
        return await api.post<{ result: { status: string; message?: string } }>(
          `/product-knowledge/${encodeURIComponent(sku)}/enrich/`,
          { force: opts?.force ?? false },
        );
      } catch (err) {
        const locked = toEnrichLockedError(err);
        if (locked) throw locked;
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["product-knowledge", sku] });
      void qc.invalidateQueries({ queryKey: ["product-knowledge-evidence", sku] });
      // Cross-Tab-Fix (Koordinator-Review, blocking): writeTruth() (service.ts) schreibt
      // stock_keywords — der Geschwister-Tab "Keywords", dessen Badge-Zaehler + Completeness-
      // Score in GrundartikelDetail.tsx dauerhaft via useKeywords gemountet ist. Ohne diese
      // Invalidierung sieht der Operator bis zu staleTime lang veraltete Zaehler/Inhalte.
      // Query-Key exakt aus api/stockSubresources.ts uebernommen (nicht geraten).
      // B1 (2026-07-20): der ANALOGE Aufruf fuer "characteristics" ist entfallen -- writeTruth
      // schreibt seit Migration 0101 KEINE stock_characteristics mehr (KI-Merkmale leben jetzt
      // in product_knowledge.ai_characteristics, bereits Teil der obigen "product-knowledge"-
      // Invalidierung). Der Merkmale-Tab (DR-gemastert) ist von einer Anreicherung unberuehrt.
      void qc.invalidateQueries({ queryKey: ["stock", sku, "keywords"] });
    },
  });
}

export function useRestoreKnowledgeVersion(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionNo: number) =>
      api.post<KnowledgeHead>(`/product-knowledge/${encodeURIComponent(sku)}/versions/${versionNo}/restore/`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["product-knowledge", sku] });
      // Gleicher Cross-Tab-Fix wie useEnrichKnowledge: restoreVersion() schreibt stock_keywords
      // zurueck. B1: KEIN stock_characteristics mehr (siehe Kommentar oben).
      void qc.invalidateQueries({ queryKey: ["stock", sku, "keywords"] });
    },
  });
}

export function useUpdateProductKnowledge(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UnderstandingPatch) =>
      api.post<KnowledgeHead>(`/product-knowledge/${encodeURIComponent(sku)}/`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["product-knowledge", sku] });
    },
  });
}
